import { Checkbox, FormControlLabel } from '@mui/material';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { addElementsToSelection, removeElementsFromSelection } from './helpers';

type Props = {
    selectedNodeValues: string[];
    setSelectedNodeValues: (n: string[]) => void;
    allSelectableNodeValues: string[];
};

const SelectAllCheckbox = ({
    selectedNodeValues,
    setSelectedNodeValues,
    allSelectableNodeValues,
}: Props) => {
    const { t } = useTranslation();
    const selectedNodeValuesSet = useMemo(
        () => new Set(selectedNodeValues),
        [selectedNodeValues],
    );

    const areAllNodesSelected = useMemo(() => {
        return allSelectableNodeValues.every((value) =>
            selectedNodeValuesSet.has(value),
        );
    }, [allSelectableNodeValues, selectedNodeValuesSet]);

    const isNoNodeSelected = useMemo(
        () =>
            allSelectableNodeValues.every(
                (value) => !selectedNodeValuesSet.has(value),
            ),
        [allSelectableNodeValues, selectedNodeValuesSet],
    );

    const isIndeterminate = !isNoNodeSelected && !areAllNodesSelected;

    const handleSelectAll = () => {
        const newSelectedValues = areAllNodesSelected
            ? removeElementsFromSelection(
                  selectedNodeValues,
                  allSelectableNodeValues,
              )
            : addElementsToSelection(
                  selectedNodeValues,
                  allSelectableNodeValues,
              );
        setSelectedNodeValues(newSelectedValues);
    };

    return (
        <FormControlLabel
            control={
                <Checkbox
                    indeterminate={isIndeterminate}
                    checked={areAllNodesSelected}
                    onClick={() => handleSelectAll()}
                />
            }
            label={t('common.selectAll')}
        />
    );
};

export default SelectAllCheckbox;
